import React, { useState, useEffect } from "react";
import { ref, onValue, update, query, get } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";
import { auth, database } from "../Config/firebase";
import {
  X,
  Bell,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
} from "lucide-react";

export default function AlertsUsers() {
  const [user, setUser] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [modalClosing, setModalClosing] = useState(false);

  useEffect(() => {
    // Escuchar cambios en la autenticación
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(currentUser === null);
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    // Solo consultar notificaciones si el usuario está autenticado
    if (!user) return;

    // Consultar notificaciones en el nodo App/alerts_users
    const alertsRef = ref(database, "App/alerts_users");

    const unsubscribeAlerts = onValue(
      alertsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (!data) {
          setAlerts([]);
          setLoading(false);
          return;
        }

        // Consultar los IDs de alertas que el usuario ya ha leído
        const userReadAlertsRef = ref(database, `users/${user.uid}/readAlerts`);
        get(userReadAlertsRef)
          .then((readSnapshot) => {
            const readAlerts = readSnapshot.val() || {};

            // Convertir los datos a un array y filtrar alertas no leídas para este usuario
            const alertsList = Object.entries(data)
              .map(([id, alert]) => ({
                id,
                ...alert,
              }))
              .filter((alert) => {
                // Verificar si la alerta está dirigida a este usuario
                const isTargeted =
                  !alert.recipientIds ||
                  alert.recipientIds === "all" ||
                  (Array.isArray(alert.recipientIds) &&
                    alert.recipientIds.includes(user.uid));

                // Verificar si la alerta no está expirada
                const isNotExpired =
                  !alert.expiresAt || alert.expiresAt > Date.now();

                // Verificar si la alerta no ha sido leída
                const isNotRead = !readAlerts[alert.id];

                return isTargeted && isNotExpired && isNotRead;
              })
              .sort((a, b) => b.createdAt - a.createdAt); // Ordenar del más reciente al más antiguo

            setAlerts(alertsList);

            // Si hay alertas disponibles para mostrar y no se está mostrando modal, mostrar la primera
            if (alertsList.length > 0 && !showModal && !currentAlert) {
              setCurrentAlert(alertsList[0]);
              setShowModal(true);
            }

            setLoading(false);
          })
          .catch((err) => {
            setError(err.message);
            setLoading(false);
          });
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribeAlerts();
  }, [user]);

  const markAsRead = (alertId) => {
    if (!user) return;

    // Actualizar en Firebase que el usuario ha leído esta alerta
    const userReadAlertsRef = ref(
      database,
      `users/${user.uid}/readAlerts/${alertId}`
    );
    update(ref(database, `users/${user.uid}/readAlerts`), {
      [alertId]: Date.now(),
    })
      .then(() => {
        // Actualizar el estado local
        setAlerts(alerts.filter((alert) => alert.id !== alertId));
      })
      .catch((err) => {
        console.error("Error al marcar la alerta como leída:", err);
      });
  };

  const handleCloseModal = () => {
    if (!currentAlert) return;

    setModalClosing(true);

    // Esperar a que termine la animación antes de cerrar completamente
    setTimeout(() => {
      markAsRead(currentAlert.id);
      setShowModal(false);
      setCurrentAlert(null);
      setModalClosing(false);
    }, 300);
  };

  if (loading) {
    return null; // No mostrar nada mientras carga
  }

  if (error) {
    return (
      <div className="text-red-500 p-4">
        Error al cargar notificaciones: {error}
      </div>
    );
  }

  // Si no hay alertas o el modal no está abierto, mostrar el botón flotante si hay alertas
  if (!showModal || !currentAlert) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        {alerts.length > 0 && (
          <button
            onClick={() => {
              setCurrentAlert(alerts[0]);
              setShowModal(true);
            }}
            className="bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
            title="Ver notificaciones"
          >
            <Bell className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {alerts.length}
            </span>
          </button>
        )}
      </div>
    );
  }

  // Determinar el estilo y el icono según el tipo de alerta
  let alertStyles = "";
  let headerBg = "bg-blue-500";
  let buttonBg = "bg-blue-500 hover:bg-blue-600";
  let AlertIcon = Info;

  switch (currentAlert.type) {
    case "warning":
      alertStyles = "border-yellow-500";
      headerBg = "bg-yellow-500";
      buttonBg = "bg-yellow-500 hover:bg-yellow-600";
      AlertIcon = AlertTriangle;
      break;
    case "error":
      alertStyles = "border-red-500";
      headerBg = "bg-red-500";
      buttonBg = "bg-red-500 hover:bg-red-600";
      AlertIcon = AlertCircle;
      break;
    case "success":
      alertStyles = "border-green-500";
      headerBg = "bg-green-500";
      buttonBg = "bg-green-500 hover:bg-green-600";
      AlertIcon = CheckCircle;
      break;
    default:
      alertStyles = "border-blue-500";
    // Mantener estilos por defecto (info)
  }

  // Modal de alerta con animaciones mejoradas
  return (
    <>
      {/* Overlay con animación */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 z-40 flex items-center justify-center ${
          modalClosing ? "bg-opacity-0" : "bg-opacity-50"
        }`}
        onClick={handleCloseModal}
      >
        {/* Modal con animación */}
        <div
          className={`bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 overflow-hidden transform transition-all duration-300 ${
            modalClosing ? "opacity-0 scale-95" : "opacity-100 scale-100"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header con color dinámico */}
          <div
            className={`flex items-center justify-between p-4 ${headerBg} text-white`}
          >
            <div className="flex items-center">
              <AlertIcon className="w-6 h-6 mr-2" />
              <h3 className="text-lg font-bold">
                {currentAlert.title || "Notificación"}
              </h3>
            </div>
            <button
              onClick={handleCloseModal}
              className="text-white hover:text-gray-200 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-700 text-base leading-relaxed">
              {currentAlert.message}
            </p>

            {currentAlert.imageUrl && (
              <div className="mt-4 overflow-hidden rounded-lg shadow-md">
                <img
                  src={currentAlert.imageUrl}
                  alt="Imagen de notificación"
                  className="w-full h-auto object-cover transition-transform duration-300 hover:scale-105"
                />
              </div>
            )}
          </div>

          {/* Footer con botones de acción */}
          <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t border-gray-100">
            <div className="text-sm text-gray-500">
              {new Date(currentAlert.createdAt).toLocaleDateString()}
            </div>
            <div className="flex space-x-3">
              {currentAlert.link && (
                <a
                  href={currentAlert.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`px-4 py-2 rounded text-white font-medium transition-all duration-300 ${buttonBg} shadow-md hover:shadow-lg`}
                  onClick={(e) => e.stopPropagation()}
                >
                  Más información
                </a>
              )}
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded font-medium shadow-md hover:bg-gray-300 transition-all duration-300 hover:shadow-lg"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
